import type { JSX } from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useEditUserSubmit } from '../hooks/useEditUserSubmit';
import { EditUserForm } from './EditUserForm';
import { editUserSchema, type EditUserFormData } from './EditUserForm.schema';
import type { CompanyRoleName, CompanyUser } from '../types/company-admin.types';

interface Props {
  user: CompanyUser | null;
  onClose: () => void;
}

function getCompanyRole(roles: string[]): CompanyRoleName {
  if (roles.includes('ADMIN')) return 'ADMIN';
  if (roles.includes('MANAGER')) return 'MANAGER';
  return 'AGENT';
}

export function EditCompanyUserDialog({ user, onClose }: Props): JSX.Element | null {
  const originalRole = user ? getCompanyRole(user.roles) : 'AGENT';
  const { submit, isLoading } = useEditUserSubmit(user?.id ?? '', originalRole, onClose);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: { firstName: '', lastName: '', phone: '', role: 'AGENT' },
  });

  useEffect(() => {
    if (user)
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone ?? '',
        role: getCompanyRole(user.roles),
      });
  }, [user, reset]);

  if (!user) return null;

  return (
    <Dialog open={user !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <EditUserForm
          register={register}
          handleSubmit={handleSubmit}
          errors={errors}
          setValue={setValue}
          watch={watch}
          submit={submit}
          onClose={onClose}
          isLoading={isLoading}
        />
      </DialogContent>
    </Dialog>
  );
}
